import { useStore } from 'zustand';

import { applicableMeasures, formatMeasurement, type MeasureKind } from '@/domain/construction';
import { type EditorStore } from '@/store';

import { PanelSection } from './Inspector';

/**
 * The measurements table.
 *
 * Values here are not a copy of anything. A measurement is an ordinary node in the
 * construction graph, so it re-evaluates when its targets move; this panel reads the
 * evaluated scene and formats it. There is no refresh path, and therefore no way for the
 * table to disagree with the canvas.
 *
 * ## Undefined measurements are shown, not dropped
 *
 * When a construction degenerates — the three points of an angle become collinear, a
 * circle collapses — its measurement evaluates to nothing. The row stays, showing a dash.
 * Removing it would make the object silently disappear from the user's inventory of
 * things they created, and reappear later with no explanation.
 *
 * ## Why the buttons are generated
 *
 * Which measurements a selection affords is decided by trying them, in
 * `applicableMeasures`. The panel therefore cannot offer a measurement that would come
 * back empty, and it cannot fall behind when the engine learns a new one.
 */

/**
 * What each measurement is called, in the words a Vietnamese lesson uses.
 *
 * `độ dài` and `khoảng cách` are two different things and the pair is worth spelling out: the
 * first is the length of a segment that exists in the figure, the second is between two objects
 * that need not be joined. A panel that called both "độ dài" would offer a teacher two identical
 * buttons.
 */
const MEASURE_LABELS: Readonly<Record<MeasureKind, string>> = {
  length: 'Độ dài',
  distance: 'Khoảng cách',
  angle: 'Góc',
  area: 'Diện tích',
  perimeter: 'Chu vi',
  radius: 'Bán kính',
  diameter: 'Đường kính',
  coordinates: 'Toạ độ',
  slope: 'Hệ số góc',
  'vector-components': 'Toạ độ vectơ',
};

export interface MeasurementsPanelProps {
  readonly store: EditorStore;
}

export function MeasurementsPanel({ store }: MeasurementsPanelProps) {
  const document = useStore(store, (state) => state.document);
  const scene = useStore(store, (state) => state.scene);
  const selection = useStore(store, (state) => state.selection);
  const commands = useStore(store, (state) => state.commands);

  // The type predicate is what lets the row read `source.measure` without a cast.
  const rows = [...document.entities.values()].filter(
    (entity): entity is typeof entity & { source: { op: 'measurement'; measure: MeasureKind } } =>
      entity.source.op === 'measurement'
  );

  // The selection in the order the user picked it, which is what "angle A-V-B" needs.
  const selected = [...selection];
  const offered =
    selected.length === 0
      ? []
      : applicableMeasures(selected.map((id) => scene.values.get(id) ?? null));

  if (rows.length === 0 && offered.length === 0) return null;

  return (
    <PanelSection
      title="Đo đạc"
      {...(rows.length === 0 ? {} : { badge: String(rows.length) })}
      flush
    >
      {rows.length > 0 && (
        <div style={{ maxHeight: '28vh', overflowY: 'auto' }}>
          {rows.map((entity) => {
            /*
             * A measurement whose construction has degenerated evaluates to nothing. The
             * row stays and shows a dash rather than vanishing from the list.
             */
            const value = scene.values.get(entity.id) ?? null;
            return (
              <MeasurementRow
                key={entity.id}
                name={entity.label ?? MEASURE_LABELS[entity.source.measure]}
                text={
                  value !== null && value.kind === 'measurement'
                    ? formatMeasurement(value.value)
                    : null
                }
                selected={selection.has(entity.id)}
                onSelect={() => {
                  commands.select([entity.id]);
                }}
              />
            );
          })}
        </div>
      )}

      {offered.length > 0 && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 4,
            padding: 8,
            borderTop: rows.length > 0 ? '1px solid var(--es-hairline)' : 'none',
          }}
        >
          {offered.map((kind) => (
            <button
              key={kind}
              type="button"
              onClick={() => {
                commands.addMeasurement(kind);
              }}
              className="es-btn"
              style={{ padding: '3px 8px', fontSize: 11 }}
            >
              + {MEASURE_LABELS[kind]}
            </button>
          ))}
        </div>
      )}
    </PanelSection>
  );
}

function MeasurementRow({
  name,
  text,
  selected,
  onSelect,
}: {
  readonly name: string;
  readonly text: string | null;
  readonly selected: boolean;
  readonly onSelect: () => void;
}) {
  return (
    <button
      type="button"
      className={selected ? 'es-row es-row--on' : 'es-row'}
      aria-pressed={selected}
      onClick={onSelect}
      style={{
        alignItems: 'baseline',
        justifyContent: 'space-between',
        borderRadius: 0,
        padding: '4px 12px',
      }}
    >
      <span style={{ fontSize: 11, fontWeight: 400, opacity: 0.75 }}>{name}</span>
      <span
        className="es-numeric"
        style={{ fontSize: 12, color: text === null ? '#c2410c' : 'inherit' }}
        title={text === null ? 'Không xác định với hình hiện tại' : undefined}
      >
        {text ?? '—'}
      </span>
    </button>
  );
}
